import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { APP_BAR_AVATAR_ME } from '@containers/Layout/AppBar/avatar.query';
import {
  FileDocumentObjectTypes,
  FileDocumentPermissionTypes,
  FileDocumentTypes,
} from '@generated/graphql';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';
import { FileTypes } from '@ovComponents/2-component/droppableFileInput/fileTypes';
import { InlineImage } from '@ovComponents/2-component/inlineImage/inlineImage';
import { base64ToFile } from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { UserContext } from '@providers/userContextProvider';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  useTheme,
} from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHANGE_AVATAR_MODAL_CREATE_FILE_DOCUMENT,
  CHANGE_AVATAR_MODAL_DELETE_FILE_DOCUMENT,
  CHANGE_AVATAR_MODAL_FETCH_FILE_UPLOAD_URL,
  CHANGE_AVATAR_MODAL_ME,
  CHANGE_AVATAR_MODAL_UPDATE_AVATAR,
} from './changeAvatarModal.query';

export type ChangeAvatarModalProp = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const ChangeAvatarModal = ({ open, setOpen }: ChangeAvatarModalProp) => {
  const { t } = useTranslation(['components', 'shared']);
  const theme = useTheme();
  const [newAvatar, setNewAvatar] = useState<string | null>(null);
  const { userContext } = useContext(UserContext);
  const { data: meData, loading: isMeQueryLoading } = useQuery(
    CHANGE_AVATAR_MODAL_ME,
    { skip: !open },
  );
  const avatar = meData?.me?.avatar;
  const isMePending = isMeQueryLoading && !meData;
  const [loading, setLoading] = useState(false);
  const [fetchFileUploadUrl] = useLazyQuery(
    CHANGE_AVATAR_MODAL_FETCH_FILE_UPLOAD_URL,
    { fetchPolicy: 'no-cache' },
  );
  const [createFileDocument] = useMutation(
    CHANGE_AVATAR_MODAL_CREATE_FILE_DOCUMENT,
  );
  const [deleteFileDocument] = useMutation(
    CHANGE_AVATAR_MODAL_DELETE_FILE_DOCUMENT,
  );
  const [updateOrganizationUserAvatar] = useMutation(
    CHANGE_AVATAR_MODAL_UPDATE_AVATAR,
  );

  const handleClose = () => {
    setOpen(false);
    setNewAvatar(null);
  };

  const doUpload = async () => {
    if (!newAvatar || !userContext.id) return;
    setLoading(true);
    const uploadingFile = base64ToFile(newAvatar, 'avatar');
    const createFileInput = {
      objectType: FileDocumentObjectTypes.OrganizationUser,
      objectId: userContext.id,
      fileName: 'avatar',
      type: FileDocumentTypes.Avatar,
    };

    const queryResult = await fetchFileUploadUrl({
      variables: {
        input: {
          ...createFileInput,
          generateUniqueFileName: true,
        },
      },
    });
    const upload = queryResult.data?.fetchFileUploadUrl;
    const uploadUrl = upload?.temporarySignedURL;
    const timestamp = upload?.timestamp;
    if (!uploadUrl || queryResult.error) {
      setLoading(false);
      return;
    }

    try {
      const uploaded = await fetch(
        new Request(uploadUrl, { method: 'PUT', body: uploadingFile }),
      );
      if (!uploaded.ok) {
        throw new Error(`${uploaded.status} ${uploaded.statusText}`);
      }
    } catch {
      setLoading(false);
      return;
    }

    await createFileDocument({
      variables: {
        input: {
          ...createFileInput,
          name: uploadingFile.name,
          mediaType: uploadingFile.type,
          permissionType: FileDocumentPermissionTypes.Public,
          sourceType: 'ORGANIZATION_USER',
          timestamp,
        },
      },
      onCompleted(data) {
        const fileDocumentId = data.createFileDocument.fileDocument?.id;
        if (!fileDocumentId) {
          setLoading(false);
          return;
        }
        void updateOrganizationUserAvatar({
          variables: { input: { avatar: fileDocumentId } },
          onCompleted: () => {
            setLoading(false);
            handleClose();
          },
          refetchQueries: [APP_BAR_AVATAR_ME, CHANGE_AVATAR_MODAL_ME],
        });
      },
    });
  };

  const setAvatar = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setNewAvatar(reader.result as string);
    };
  };

  const deleteFile = async () => {
    if (!avatar) return;
    await deleteFileDocument({
      variables: { fileDocumentId: avatar },
      onCompleted: () => {
        setNewAvatar(null);
        void updateOrganizationUserAvatar({
          variables: { input: { avatar: null } },
          refetchQueries: [APP_BAR_AVATAR_ME, CHANGE_AVATAR_MODAL_ME],
        });
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>
        {t('updateAvatar.title')}
        <IconButton aria-label={t('shared:close')} onClick={handleClose}>
          <CloseIcon fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isMePending && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Skeleton variant='circular' width={100} height={100} />
          </Box>
        )}
        {!isMePending && !avatar && (
          <DroppableFileInput
            onFileChosen={setAvatar}
            acceptedTypes={[
              FileTypes.JPEG,
              FileTypes.JPG,
              FileTypes.PNG,
              FileTypes.HEIC,
            ]}
            fileMaxSize={3}
          />
        )}
        {!isMePending && avatar && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ mb: 1 }}>
              <InlineImage
                fileDocumentId={avatar}
                style={{
                  height: '100px',
                  width: '100px',
                  borderRadius: theme.borderRadius.round,
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Button
              onClick={() => void deleteFile()}
              variant='text'
              color='error'
            >
              {t('shared:delete')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => void doUpload()}
          disabled={!newAvatar || loading}
        >
          {t('shared:save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
